/**
 * MessageBottomBar 组件
 * 包含 Logo、复制按钮和测试按钮的底栏组件
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { TempoLogo } from './TempoLogo';
import { addToastAtom } from '../../../../atoms/v0/uiAtoms';
import { messagesAtom } from '../../../../atoms/v3/chatAtoms';
import { Message, ProcessStep } from '../../../../types';
import { messageUtils } from '../../../../atoms/v3/messageUtils';
// 定义工具错误状态枚举（从后端复制）
enum ToolErrorStatus {
  SYSTEM_ERROR = 'system_error',
  VALIDATION_ERROR = 'validation_error',
  UPDATE_ERROR = 'update_error',
  FETCH_PROJECT_ERROR = 'fetch_project_error',
  FETCH_ERROR = 'fetch_error',
}

interface MessageBottomBarProps {
  message: Message;
  hasContent: boolean;
  hasProcessStep: boolean;
  messageContent: string;
}

export const MessageBottomBar: React.FC<MessageBottomBarProps> = ({
  message,
  hasContent,
  hasProcessStep,
  messageContent,
}) => {
  const { t } = useTranslation(['v3/chat']);
  const addToast = useSetAtom(addToastAtom);
  const setMessages = useSetAtom(messagesAtom);

  // 检查消息是否为错误状态
  const isError = message.status === 'error';

  // 复制功能
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageContent);
      addToast({
        title: t('v3/chat:actions.copied'),
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      addToast({
        title: t('v3/chat:actions.copyFailed'),
        status: 'error',
        duration: 3000,
      });
    }
  };

  // 测试功能：添加各种类型的 tool_error 到 processLog
  const handleTestToolErrors = () => {
    if (!message.id) return;

    const errorTypes = Object.values(ToolErrorStatus);
    const testProcessSteps: ProcessStep[] = errorTypes.map((errorType, index) => ({
      type: 'tool_error',
      toolId: `test-tool-${index}`,
      toolName: `TestTool${index + 1}`,
      status: errorType,
      result: {
        message: `测试错误消息 - ${errorType}`,
        details: `详细错误信息：这是一个 ${errorType} 类型的测试错误，用于验证 ToolError 组件的渲染效果。`
      },
      timestamp: new Date().toISOString(),
      sequence: (message.processStep?.length || 0) + index
    }));
    setMessages((prev) =>
      messageUtils.updateProcessLog(prev, message.conversationId || '', message.id, (processLog) => {
        return [...processLog, ...testProcessSteps];
      })
    );

    addToast({
      title: '已添加测试错误',
      description: `添加了 ${errorTypes.length} 个不同类型的工具错误`,
      status: 'info',
      duration: 3000,
    });
  };

  return (
    <div className="flex items-center justify-between mt-2">
      {/* 左侧：Logo */}
      <div className="flex items-center">
        <TempoLogo
          isLoading={!hasContent && !isError}
          isError={isError}
          size="sm"
        />
        {!hasContent && !isError && !hasProcessStep && (
          <span className="ml-2 text-xs text-ios-gray dark:text-ios-dark-gray">
            {t('v3/chat:analyzingIntent')}
          </span>
        )}
      </div>

      {/* 右侧：按钮组 */}
      <div className="flex items-center space-x-2">
        {/* 测试按钮 - 手动不可见 */}
        {/* eslint-disable-next-line no-constant-binary-expression */}
        { false && (
          <button
            onClick={handleTestToolErrors}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors opacity-70 hover:opacity-100"
            title="测试工具错误"
          >
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </button>
        )}

        {/* 复制按钮 */}
        {hasContent && (
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors opacity-70 hover:opacity-100"
            title={t('v3/chat:actions.copy')}
          >
            <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageBottomBar;
